/**
 * Admin view types.
 * Shared between relay and dashboard.
 */

import type { TradingRoom } from './rooms.js';

// ── Portfolio / Room summaries ────────────────────────────────────────────────

export interface RoomSummary {
  room: TradingRoom;
  positionCount: number;
  orderCount: number;
  totalPnl: number;
  defensePosture: string;
  activeMembers: number;
  lastActivity: string;
}

export interface OrgPortfolioSummary {
  rooms: RoomSummary[];
  totalPnl: number;
  totalExposure: number;
  activeTraders: number;
  totalPositions: number;
  totalOrders: number;
}

// ── Trader stats ──────────────────────────────────────────────────────────────

export interface TraderStats {
  userId: string;
  displayName: string;
  avatarUrl: string | null;
  tradeCount24h: number;
  pnl24h: number;
  activeRooms: number;
  lastTradeAt: string | null;
}

// ── Trade log ─────────────────────────────────────────────────────────────────

export interface TradeLogEntry {
  id: string;
  roomId: string;
  roomName: string;
  userId: string;
  displayName: string;
  credentialSetId: string | null;
  action: 'order_placed' | 'order_cancelled' | 'order_modified';
  ticker: string | null;
  side: 'yes' | 'no' | null;
  quantity: number | null;
  priceCents: number | null;
  orderId: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
}

export interface TradeLogQuery {
  roomId?: string;
  userId?: string;
  ticker?: string;
  limit?: number;
  offset?: number;
}

// ── Credential sets (admin-visible shape) ─────────────────────────────────────

export interface CredentialSet {
  id: string;
  orgId: string;
  label: string;
  accessKey: string;
  environment: 'prod' | 'demo';
  addedBy: string | null;
  addedByName: string | null;
  createdAt: string;
}

export interface CreateCredentialSetRequest {
  label: string;
  accessKey: string;
  privateKey: string; // plaintext — encrypted server-side
  environment: 'prod' | 'demo';
}

// ── User management ───────────────────────────────────────────────────────────

export interface UserListEntry {
  id: string;
  email: string;
  displayName: string;
  avatarUrl: string | null;
  role: 'admin' | 'manager' | 'trader';
  maxContracts: number | null;
  maxPositions: number | null;
  createdAt: string;
  lastLoginAt: string | null;
}

export interface InviteUserRequest {
  email: string;
  role?: 'admin' | 'manager' | 'trader';
}

export interface UpdateUserRoleRequest {
  role: 'admin' | 'manager' | 'trader';
}

// ── Trader limits ─────────────────────────────────────────────────────────────

export interface UpdateTraderLimitsRequest {
  maxContracts?: number | null; // null = clear (unlimited)
  maxPositions?: number | null;
  traderRules?: TraderRules | null; // null clears entire rules object
}

/** Sport taxonomy for trader-rules sport allowlist. Mirrors the parser-side
 *  classifier in the relay. Adding a sport requires updating the relay's
 *  classifier + this enum + the Manager UI's sport-list source. */
export type Sport =
  | 'MLB'
  | 'NBA'
  | 'WNBA'
  | 'NFL'
  | 'NHL'
  | 'NCAA'
  | 'Tennis'
  | 'Soccer'
  | 'CFB'
  | 'UFC'
  | 'Boxing'
  | 'Cricket'
  | 'Esports'
  | 'Golf';

export const KNOWN_SPORTS: readonly Sport[] = [
  'MLB',
  'NBA',
  'WNBA',
  'NFL',
  'NHL',
  'NCAA',
  'Tennis',
  'Soccer',
  'CFB',
  'UFC',
  'Boxing',
  'Cricket',
  'Golf',
  'Esports',
  'Golf',
];

/** Per-trader rule object stored as JSONB on users.trader_rules. NULL on
 *  the column means "no rules configured" (legacy / fully unrestricted). */
export interface TraderRules {
  /** Whitelist of sport codes. null = no sport restriction. Empty array is
   *  invalid and the relay refuses to persist it. */
  allowedSports: Sport[] | null;
  /** Independent AND-gate: non-sport markets (politics, crypto, etc.)
   *  blocked when false. */
  nonSportsEnabled: boolean;
  /** Allow new Polymarket buys. Sells/cancels always pass regardless of
   *  this flag — it's a "don't open new Polymarket exposure" gate, not
   *  a hard venue halt. */
  polymarketEnabled: boolean;
  /** Allow new Polymarket US buys (the CFTC venue, org-pooled key). SEPARATE
   *  from polymarketEnabled so a manager can allow one Polymarket and not the
   *  other. Absent = true (a kill switch, not an opt-in): sells/cancels always
   *  pass. Relay twin: relay/src/db/schema.ts TraderRules. */
  polymarketUsEnabled?: boolean;
  /** Oscar overtake/blocker sequence access. FAIL-CLOSED, unlike the other
   *  fields: null rules or a missing key = disabled (opt-in capability, not
   *  a restriction). Relay twin: relay/src/db/schema.ts TraderRules. */
  oscarEnabled?: boolean;
  /** Armed one-click placement access. FAIL-CLOSED: absent/null rules means
   *  disabled. Relay twin: relay/src/db/schema.ts TraderRules. */
  oneClickEnabled?: boolean;
  /** Live Trading V2 surface access (D-020): derivative spread/total one-click,
   *  two-sided ladders, A/D hotkeys, multi-line view. FAIL-CLOSED like
   *  oneClickEnabled — absent/null rules means disabled, which is why the
   *  manager UI renders a missing key as OFF rather than as unknown.
   *  Relay twin: relay/src/db/schema.ts TraderRules. */
  lt2Enabled?: boolean;
  /** StreamRift Cockpit + Larry bot-management access. FAIL-CLOSED like
   *  oscarEnabled: absent/null rules means disabled. Gates /api/larry and the
   *  /cockpit surface. Relay twin: relay/src/db/schema.ts TraderRules. */
  cockpitEnabled?: boolean;
  /** RFQ desk access (the /rfq surface + /api/rfq routes). FAIL-CLOSED like
   *  oscarEnabled: absent/null rules means disabled. A granted trader
   *  operates only their own cards; the global kill stays operator-only.
   *  Relay twin: relay/src/db/schema.ts TraderRules. */
  rfqEnabled?: boolean;
  /** RFQ desk READ-ONLY access (the analyst grant, 2026-10-14). FAIL-CLOSED
   *  like rfqEnabled. Reads every RFQ screen and export for the whole org;
   *  the relay refuses every /api/rfq write (RFQ_READ_ONLY) and never
   *  provisions a bot. Independent of rfqEnabled. */
  rfqAnalyst?: boolean;
  /** Single-game live trading view access (/live/:gameKey — one-game board
   *  with LT2 ladders/hotkeys + game-wide pull). FAIL-CLOSED like
   *  oscarEnabled: absent/null rules means disabled. View exposure only;
   *  order capability stays with oneClickEnabled/lt2Enabled.
   *  Relay twin: relay/src/db/schema.ts TraderRules. */
  liveGameEnabled?: boolean;
  /** StreamRift Sniper access (/sniper/:gameKey screen + /api/sniper).
   *  FAIL-CLOSED like oscarEnabled; managers get NO role pass — admin or
   *  this explicit grant only (sniper arms real-money runners on org keys).
   *  Relay twin: relay/src/db/schema.ts TraderRules. */
  sniperEnabled?: boolean;
  /** Explicit per-trader ceiling on a single `lt-` tagged (one-click/hotkey)
   *  order's contract count. ABSENT IS NOT UNLIMITED and not zero: the relay
   *  uses its 10,000 default for both V1 and V2. Invalid configured values are
   *  refused (`LT2_SIZE_LIMIT_REQUIRED`). Whole contracts only, within
   *  [ONE_CLICK_CEILING_MIN, ONE_CLICK_CEILING_MAX].
   *  Relay twin: relay/src/db/schema.ts TraderRules. */
  oneClickMaxContracts?: number;
  /** TapeLab level. FAIL-CLOSED: absent = no access; "off" is expressed by
   *  omitting the key (the relay rejects null/false). Relay twin:
   *  relay/src/db/schema.ts TraderRules / services/labAccess.ts. */
  labAccess?: 'viewer' | 'researcher';
  /** Explicit ordinary-size edit above the legacy 10,000 ceiling. */
  oneClickLimitMode?: 'independent';
  /** Live Trading V3 Attack (W/E) hotkey access. FAIL-CLOSED like lt2Enabled
   *  — absent/null rules means disabled. Attack orders additionally require
   *  the full LT2 stack and a valid effective Attack ceiling; the manager UI
   *  reports a granted-but-inactive chip the same way the LT2 chip does.
   *  Relay twin: relay/src/db/schema.ts TraderRules. */
  attackEnabled?: boolean;
  /** Explicit per-trader ceiling on a single `__attack` tagged order's
   *  contract count. ABSENT IS NOT UNLIMITED: defaults to 1,000 independently
   *  of one-click. Configured legacy allowances stay unchanged. Whole
   *  contracts only, within [ATTACK_CEILING_MIN, ATTACK_CEILING_MAX].
   *  Relay twin: relay/src/db/schema.ts TraderRules. */
  attackMaxContracts?: number;
  /** Per-trader RFQ limits (V58). Positive integers; money in cents. Absent =
   *  not set; a trader quoting on their own RFQ desk is refused until all four
   *  are set. Relay twin: relay/src/db/schema.ts TraderRules. */
  rfqMaxOpenLiabilityCents?: number;
  rfqMaxContractsPerQuote?: number;
  rfqDailyLossStopCents?: number;
  rfqMaxQuotesPerMin?: number;
  /** Absent preserves legacy effective limits until a deliberate sizing edit. */
  attackLimitMode?: 'independent';
}

/** Bounds the relay enforces on `TraderRules.oneClickMaxContracts`
 *  (relay/src/routes/manager.ts parseTraderRulesField, against
 *  relay/src/kalshi/oneClickPolicy.ts ONE_CLICK_HARD_MAX_CONTRACTS). A value
 *  outside this range is rejected with a 400, so the manager UI refuses it
 *  before the round-trip. Declared here, beside the field it bounds, rather
 *  than imported from stores/oneClickStore — that module is the trading
 *  store's hard client clamp and pulls the whole order-submission graph with
 *  it, which has no business in the manager plane. */
export const ONE_CLICK_CEILING_MIN = 1;
export const ONE_CLICK_LEGACY_CEILING_MAX = 10_000;
/** Representation bound of trades.count, not a recommended risk ceiling. */
export const ONE_CLICK_CEILING_MAX = 2_147_483_647;
export const ONE_CLICK_DEFAULT_MAX_CONTRACTS = 10_000;

/** Relay mirror: default only an absent value, never malformed configuration. */
export function effectiveOneClickCeiling(rules: { oneClickMaxContracts?: number; oneClickLimitMode?: 'independent' } | null | undefined): number | null {
  if (rules?.oneClickLimitMode !== undefined && rules.oneClickLimitMode !== 'independent') return null;
  const value = rules?.oneClickMaxContracts === undefined
    ? ONE_CLICK_DEFAULT_MAX_CONTRACTS : rules.oneClickMaxContracts;
  if (!Number.isSafeInteger(value) || value <= 0) return null;
  if (rules?.oneClickLimitMode === 'independent') return value <= ONE_CLICK_CEILING_MAX ? value : null;
  return Math.min(value, ONE_CLICK_LEGACY_CEILING_MAX);
}

/** Bounds the relay enforces on `TraderRules.attackMaxContracts`
 *  (relay/src/routes/manager.ts parseTraderRulesField, against
 *  relay/src/kalshi/attackPolicy.ts ATTACK_STORAGE_MAX_CONTRACTS). Same
 *  declared-here reasoning as the one-click bounds above. */
export const ATTACK_CEILING_MIN = 1;
export const ATTACK_DEFAULT_MAX_CONTRACTS = 1_000;
/** Representation bound of the relay's trades.count, not a product ceiling. */
export const ATTACK_CEILING_MAX = 2_147_483_647;

/** Mirror of relay attackPolicy; contract tests keep the two aligned. */
export function effectiveAttackCeiling(rules: TraderRules | null | undefined): number | null {
  const oneClick = effectiveOneClickCeiling(rules);
  if (oneClick == null) return null;
  if (rules?.attackLimitMode !== undefined && rules.attackLimitMode !== 'independent') return null;
  if (rules?.attackMaxContracts === undefined) return ATTACK_DEFAULT_MAX_CONTRACTS;
  const legacyOneClick = rules?.oneClickMaxContracts === undefined ? 500 : oneClick;
  const value = rules.attackMaxContracts;
  if (typeof value !== 'number' || !Number.isSafeInteger(value) || value <= 0 || value > ATTACK_CEILING_MAX) return null;
  if (rules?.attackLimitMode === 'independent') return value;
  if (rules?.attackLimitMode !== undefined) return null;
  return Math.min(value, 1_000, legacyOneClick, ONE_CLICK_LEGACY_CEILING_MAX);
}

export type LimitRejectionCode =
  | 'LIMIT_MAX_CONTRACTS'
  | 'LIMIT_MAX_POSITIONS'
  | 'LIMIT_SPORT_NOT_ALLOWED'
  | 'LIMIT_NON_SPORT_DISABLED'
  | 'LIMIT_POLYMARKET_DISABLED'
  | 'LIMIT_MARKET_SPORT_UNKNOWN';

export interface LimitRejectionBreach {
  rule: LimitRejectionCode;
  cap: number;
  current: number;
  requested: number;
}

export interface LimitRejectionResponse {
  error: string;
  code: LimitRejectionCode;
  breach: LimitRejectionBreach;
}

/** Server defaults applied on POST /api/manager/traders. */
export const TRADER_LIMIT_DEFAULTS = {
  maxContracts: 10_000,
  maxPositions: 20,
} as const;

/** Default trader_rules object applied at new-trader insert time.
 *  Sport-unrestricted, non-sports blocked (matches "not engaged in non-sport
 *  Kalshi markets" stance), Polymarket allowed. Confirmed default 2026-05-23. */
export const TRADER_RULES_DEFAULTS: TraderRules = {
  allowedSports: null,
  nonSportsEnabled: false,
  polymarketEnabled: true,
};

export interface TraderHeadroom {
  maxContracts: number | null;
  maxPositions: number | null;
  openContracts: number;
  openPositions: number;
  intentContracts: number;
  /** Quick summary of which rules are active so the LimitsWidget can show
   *  a compact suffix without re-pulling the full traderRules object. */
  rulesActive?: {
    sportRestricted: boolean;
    nonSportsBlocked: boolean;
    polymarketBlocked: boolean;
  };
}
